import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Plus, FileText, Sparkles, Eye, Copy, Trash2, Loader2, Edit, ExternalLink, Send } from 'lucide-react';
import { BASE_TEMPLATES } from '@/lib/forms/templates';
import { FormTemplate, ClientFormTemplate } from '@/lib/forms/schema';
import { DynamicFormRenderer } from '@/components/forms';

const CATEGORIES = [
  { value: 'intake', label: 'Intake Geral' },
  { value: 'family', label: 'Imigração Familiar' },
  { value: 'employment', label: 'Imigração por Trabalho' },
  { value: 'humanitarian', label: 'Humanitário/Asilo' },
  { value: 'citizenship', label: 'Cidadania' },
  { value: 'evidence', label: 'Coleta de Evidências' },
  { value: 'general', label: 'Geral' },
];

export default function FormTemplatesSettings() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'saved' | 'base'>('saved');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);
  const [isAIDialogOpen, setIsAIDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [aiPrompt, setAIPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedTemplate, setGeneratedTemplate] = useState<FormTemplate | null>(null);

  // New template form state
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    description: '',
    category: 'general',
    visa_type: '',
  });

  // Fetch saved templates
  const { data: savedTemplates, isLoading } = useQuery({
    queryKey: ['form-templates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_form_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as unknown as ClientFormTemplate[];
    }
  });

  // Save base template to database
  const saveTemplateMutation = useMutation({
    mutationFn: async (template: FormTemplate) => {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data, error } = await supabase
        .from('client_form_templates')
        .insert({
          name: template.name,
          description: template.description,
          category: template.category,
          visa_type: template.visaType || null,
          schema: template as any,
          is_base_template: true,
          created_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates'] });
      toast.success('Template salvo com sucesso!');
      setIsCreateDialogOpen(false);
    },
    onError: (error) => {
      console.error('Error saving template:', error);
      toast.error('Erro ao salvar template');
    }
  });

  // Delete template
  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('client_form_templates')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['form-templates'] });
      toast.success('Template excluído');
    },
    onError: () => {
      toast.error('Erro ao excluir template');
    }
  });

  const handleSaveBaseTemplate = (template: FormTemplate) => {
    saveTemplateMutation.mutate(template);
  };

  const handlePreview = (template: FormTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewDialogOpen(true);
  };

  const handleGenerateWithAI = async () => {
    if (!aiPrompt.trim()) {
      toast.error('Digite uma descrição do formulário');
      return;
    }

    setIsGenerating(true);
    try {
      const { data, error } = await supabase.functions.invoke('generate-form-template', {
        body: {
          action: 'create',
          prompt: aiPrompt,
          context: {
            category: newTemplate.category,
            visa_type: newTemplate.visa_type
          }
        }
      });

      if (error) throw error;
      
      if (data.template) {
        setGeneratedTemplate(data.template);
        toast.success('Template gerado! Revise e salve.');
      }
    } catch (error) {
      console.error('Error generating template:', error);
      toast.error('Erro ao gerar template. Tente novamente.');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleSaveGeneratedTemplate = async () => {
    if (!generatedTemplate) return;
    
    saveTemplateMutation.mutate(generatedTemplate);
    setGeneratedTemplate(null);
    setAIPrompt('');
    setIsAIDialogOpen(false);
  };

  const getCategoryLabel = (category: string) => {
    return CATEGORIES.find(c => c.value === category)?.label || category;
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Formulários para Clientes</h1>
          <p className="text-muted-foreground">
            Crie e gerencie formulários que serão enviados aos clientes para coleta de informações
          </p>
        </div>
        
        <div className="flex gap-2">
          <Dialog open={isAIDialogOpen} onOpenChange={setIsAIDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline">
                <Sparkles className="h-4 w-4 mr-2" />
                Criar com IA
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Criar Formulário com IA</DialogTitle>
                <DialogDescription>
                  Descreva o formulário que você quer criar e a IA vai gerar a estrutura completa
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Categoria</Label>
                    <Select
                      value={newTemplate.category}
                      onValueChange={(v) => setNewTemplate(prev => ({ ...prev, category: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map(cat => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tipo de Visto (opcional)</Label>
                    <Input
                      placeholder="Ex: I-130, N-400, EB-2 NIW"
                      value={newTemplate.visa_type}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, visa_type: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Descreva o formulário</Label>
                  <Textarea
                    placeholder="Ex: Criar um questionário de intake para casos de EB-2 NIW, coletando informações sobre educação, experiência profissional, publicações e prêmios..."
                    value={aiPrompt}
                    onChange={(e) => setAIPrompt(e.target.value)}
                    rows={4}
                  />
                </div>

                {generatedTemplate && (
                  <Card className="border-green-200 bg-green-50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Sparkles className="h-4 w-4 text-green-600" />
                        Template Gerado
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="font-medium">{generatedTemplate.name}</p>
                      <p className="text-sm text-muted-foreground">{generatedTemplate.description}</p>
                      <p className="text-sm mt-2">
                        {generatedTemplate.sections.length} seções, {' '}
                        {generatedTemplate.sections.reduce((acc, s) => acc + s.fields.length, 0)} campos
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" variant="outline" onClick={() => handlePreview(generatedTemplate)}>
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button size="sm" onClick={handleSaveGeneratedTemplate}>
                          Salvar Template
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsAIDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button onClick={handleGenerateWithAI} disabled={isGenerating || !aiPrompt.trim()}>
                  {isGenerating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4 mr-2" />
                      Gerar Template
                    </>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'saved' | 'base')}>
        <TabsList>
          <TabsTrigger value="saved">
            Meus Templates ({savedTemplates?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="base">
            Templates Base ({BASE_TEMPLATES.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="saved" className="mt-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : savedTemplates?.length === 0 ? (
            <Card className="py-12">
              <CardContent className="text-center">
                <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">Nenhum template salvo</h3>
                <p className="text-muted-foreground mb-4">
                  Comece salvando um template base ou criando um novo com IA
                </p>
                <div className="flex justify-center gap-2">
                  <Button variant="outline" onClick={() => setActiveTab('base')}>
                    Ver Templates Base
                  </Button>
                  <Button onClick={() => setIsAIDialogOpen(true)}>
                    <Sparkles className="h-4 w-4 mr-2" />
                    Criar com IA
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedTemplates?.map((template) => {
                const schema = template.schema as FormTemplate;
                return (
                  <Card key={template.id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">{template.name}</CardTitle>
                          <CardDescription className="line-clamp-2">
                            {template.description}
                          </CardDescription>
                        </div>
                        <Badge variant="secondary">
                          {getCategoryLabel(template.category || 'general')}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="text-sm text-muted-foreground mb-4">
                        {schema.sections?.length || 0} seções • {' '}
                        {schema.sections?.reduce((acc, s) => acc + (s.fields?.length || 0), 0) || 0} campos
                        {template.visa_type && (
                          <span className="ml-2">• {template.visa_type}</span>
                        )}
                      </div>
                      
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => handlePreview(schema)}>
                          <Eye className="h-4 w-4 mr-1" />
                          Preview
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-destructive hover:text-destructive"
                          onClick={() => {
                            if (confirm('Tem certeza que deseja excluir este template?')) {
                              deleteTemplateMutation.mutate(template.id);
                            }
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="base" className="mt-4">
          <div className="mb-4 p-4 bg-muted/50 rounded-lg">
            <p className="text-sm text-muted-foreground">
              Templates base são definidos no código e servem como ponto de partida. 
              Salve um para poder usá-lo com seus clientes.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {BASE_TEMPLATES.map((template) => (
              <Card key={template.id} className="hover:shadow-md transition-shadow">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{template.name}</CardTitle>
                      <CardDescription className="line-clamp-2">
                        {template.description}
                      </CardDescription>
                    </div>
                    <Badge variant="outline">
                      {getCategoryLabel(template.category)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="text-sm text-muted-foreground mb-4">
                    {template.sections.length} seções • {' '}
                    {template.sections.reduce((acc, s) => acc + s.fields.length, 0)} campos
                    {template.visaType && (
                      <span className="ml-2">• {template.visaType}</span>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => handlePreview(template)}>
                      <Eye className="h-4 w-4 mr-1" />
                      Preview
                    </Button>
                    <Button 
                      size="sm" 
                      onClick={() => handleSaveBaseTemplate(template)}
                      disabled={saveTemplateMutation.isPending}
                    >
                      <Copy className="h-4 w-4 mr-1" />
                      Usar Template
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={isPreviewDialogOpen} onOpenChange={setIsPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTemplate?.name}</DialogTitle>
            <DialogDescription>{selectedTemplate?.description}</DialogDescription>
          </DialogHeader>
          
          {selectedTemplate && (
            <div className="border rounded-lg p-4 bg-muted/30">
              <DynamicFormRenderer
                template={selectedTemplate}
                disabled={true}
                showProgress={false}
              />
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsPreviewDialogOpen(false)}>
              Fechar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
